import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Checkbox } from '@mui/material';
import { Autocomplete, IconButton } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { useQuery } from 'hooks/apollo';
import { FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_PARAM_VALUE_LIST_QUERY } from '../../../queries';
import { Close, Check } from '@mui/icons-material';
import { get, set, over } from '@totalsoft/change-tracking-react';
import { StyledTableRow, StyledTableBodyCell } from 'components/common/Styles';

const FinancialProductRulesEditDetailsOutputDictionariesTableRow = ({
  canWrite,
  financialProductRuleOutputDictionaryParamList,
  dataLens,
  index,
  onCloseEdit,
  onDelete,
  onSave,
  financialProductRuleOutputDictionaryValidation,
  listValid
}) => {
  const [t] = useTranslation();

  const data = dataLens |> get;

  const { data: financialProductRuleOutputDictionaryParamValueList } = useQuery(
    FINANCIAL_PRODUCT_RULE_OUTPUT_DICTIONARY_PARAM_VALUE_LIST_QUERY,
    {
      skip: !data.paramId,
      variables: { paramId: data.paramId }
    }
  );

  return (
    <StyledTableRow>
      {data.isInEditing ? (
        <>
          <StyledTableBodyCell>
            <Checkbox checked={data.readOnlyYN || false} onChange={() => set(dataLens.readOnlyYN)(!data.readOnlyYN)} />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Autocomplete
              valueKey={'paramId'}
              labelKey={'paramName'}
              value={{ paramName: data.paramName, paramId: data.paramId }}
              options={financialProductRuleOutputDictionaryParamList}
              onChange={event => {
                over(dataLens, oldState => ({
                  ...oldState,
                  paramId: event.paramId,
                  paramName: event.paramName,
                  filterValueId: null,
                  filterValue: null
                }));
              }}
              error={!isValid(financialProductRuleOutputDictionaryValidation.paramId)}
              helperText={getErrors(financialProductRuleOutputDictionaryValidation.paramId)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <Autocomplete
              valueKey={'filterValueId'}
              labelKey={'filterValue'}
              value={{ filterValue: data.filterValue, filterValueId: data.filterValueId }}
              options={financialProductRuleOutputDictionaryParamValueList?.financialProductRuleOutputDictionaryParamValueList ?? []}
              onChange={event => {
                over(dataLens, oldState => ({
                  ...oldState,
                  filterValueId: event.filterValueId,
                  filterValue: event.filterValue
                }));
              }}
              error={
                !isValid(financialProductRuleOutputDictionaryValidation.filterValue) ||
                !isValid(financialProductRuleOutputDictionaryValidation.filterValueId)
              }
              helperText={getErrors(financialProductRuleOutputDictionaryValidation.filterValue)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='save'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Save')}
                disabled={data.isSaving || !canWrite || !listValid}
                onClick={() => onSave(index)}
              />
              <IconButton
                type='cancel'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.CloseEdit')}
                onClick={() => onCloseEdit(index)}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      ) : (
        <>
          <StyledTableBodyCell>
            <>{data.readOnlyYN === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
          <StyledTableBodyCell>{data.paramName}</StyledTableBodyCell>
          <StyledTableBodyCell>{data.filterValue}</StyledTableBodyCell>
          <StyledTableBodyCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='edit'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Edit')}
                onClick={() => set(dataLens.isInEditing)(true)}
                disabled={!canWrite}
              />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(data.financialProductRuleOutputDictionaryId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </StyledTableBodyCell>
        </>
      )}
    </StyledTableRow>
  );
};

FinancialProductRulesEditDetailsOutputDictionariesTableRow.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  financialProductRuleOutputDictionaryParamList: PropTypes.array.isRequired,
  dataLens: PropTypes.object,
  index: PropTypes.number,
  onEdit: PropTypes.func.isRequired,
  onCloseEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  financialProductRuleOutputDictionaryValidation: PropTypes.object.isRequired,
  listValid: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsOutputDictionariesTableRow;
